import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

interface Note {
  id: number;
  content: string;
  created_by: number;
  first_name: string;
  last_name: string;
  user_email: string;
  created_at: string;
  updated_at: string;
}

interface ActivityItem {
  id: number;
  action: string;
  details: any;
  first_name: string;
  last_name: string;
  created_at: string;
}

interface NotesActivityProps {
  entityType: string;
  entityId: number;
}

const NotesActivity: React.FC<NotesActivityProps> = ({ entityType, entityId }) => {
  const { token, user } = useAuth();
  const [activeTab, setActiveTab] = useState<'notes' | 'activity'>('notes');
  const [notes, setNotes] = useState<Note[]>([]);
  const [activity, setActivity] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editContent, setEditContent] = useState('');

  const fetchNotes = useCallback(async () => {
    if (!token) return;
    try {
      const resp = await fetch(`/api/notes/${entityType}/${entityId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (resp.ok) setNotes(await resp.json());
    } catch (err) {
      console.error('Failed to fetch notes:', err);
    }
  }, [token, entityType, entityId]);

  const fetchActivity = useCallback(async () => {
    if (!token) return;
    try {
      const resp = await fetch(`/api/notes/${entityType}/${entityId}/activity`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (resp.ok) setActivity(await resp.json());
    } catch (err) {
      console.error('Failed to fetch activity:', err);
    }
  }, [token, entityType, entityId]);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchNotes(), fetchActivity()]).finally(() => setLoading(false));
  }, [fetchNotes, fetchActivity]);

  const handleAddNote = async () => {
    if (!newNote.trim() || !token || saving) return;
    setSaving(true);
    try {
      const resp = await fetch(`/api/notes/${entityType}/${entityId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: newNote.trim() }),
      });
      if (!resp.ok) throw new Error('Failed');
      const note = await resp.json();
      setNotes(prev => [note, ...prev]);
      setNewNote('');
    } catch (err) {
      console.error('Failed to add note:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateNote = async (noteId: number) => {
    if (!editContent.trim() || !token) return;
    try {
      const resp = await fetch(`/api/notes/${noteId}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: editContent.trim() }),
      });
      if (!resp.ok) throw new Error('Failed');
      const updated = await resp.json();
      setNotes(prev => prev.map(n => n.id === noteId ? { ...n, ...updated } : n));
      setEditingId(null);
      setEditContent('');
    } catch (err) {
      console.error('Failed to update note:', err);
    }
  };

  const handleDeleteNote = async (noteId: number) => {
    if (!token) return;
    try {
      const resp = await fetch(`/api/notes/${noteId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error('Failed');
      setNotes(prev => prev.filter(n => n.id !== noteId));
    } catch (err) {
      console.error('Failed to delete note:', err);
    }
  };

  const formatTime = (dateStr: string) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHrs = Math.floor(diffMin / 60);
    if (diffHrs < 24) return `${diffHrs}h ago`;
    const diffDays = Math.floor(diffHrs / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined });
  };

  const getActivityLabel = (action: string, details: any) => {
    const jobLabel = details?.job_title ? ` "${details.job_title}"` : '';
    const campaignLabel = details?.campaign_name ? ` "${details.campaign_name}"` : '';
    const stageLabel = details?.stage_name ? ` (${details.stage_name})` : '';

    switch (action) {
      case 'created': return 'Created this record';
      case 'note_added': return 'Added a note';
      case 'added_to_pipeline': return `Added to job${jobLabel}${stageLabel}`;
      case 'moved_stage': return `Moved from ${details?.from_stage || '?'} to ${details?.to_stage || '?'}${jobLabel}`;
      case 'removed_from_pipeline': return `Removed from job${jobLabel}`;
      case 'status_changed': return `Status changed to ${details?.status || '?'}`;
      case 'bulk_added_to_pipeline': return `Added ${details?.count || '?'} entities to pipeline`;
      case 'shortlisted': return `Shortlisted ${details?.count || '?'} entities`;
      case 'added_to_bd_campaign': return `Added to BD campaign${campaignLabel}${stageLabel}`;
      case 'bd_campaign_moved_stage': return `Moved from ${details?.from_stage || '?'} to ${details?.to_stage || '?'} in campaign${campaignLabel}`;
      case 'removed_from_bd_campaign': return `Removed from BD campaign${campaignLabel}`;
      case 'bulk_added_to_campaign': return `Added ${details?.count || '?'} targets to campaign`;
      case 'sent_to_sourcewhale': return 'Sent to SourceWhale';
      default: return action.replace(/_/g, ' ');
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Tab bar */}
      <div className="flex border-b border-gray-200">
        <button
          onClick={() => setActiveTab('notes')}
          className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors ${
            activeTab === 'notes'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Notes {notes.length > 0 && <span className="ml-1 text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-full">{notes.length}</span>}
        </button>
        <button
          onClick={() => setActiveTab('activity')}
          className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors ${
            activeTab === 'activity'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Activity {activity.length > 0 && <span className="ml-1 text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-full">{activity.length}</span>}
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
        </div>
      ) : activeTab === 'notes' ? (
        <div className="flex-1 flex flex-col">
          {/* Add note input */}
          <div className="p-4 border-b border-gray-100">
            <textarea
              value={newNote}
              onChange={e => setNewNote(e.target.value)}
              placeholder="Write a note..."
              rows={2}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none"
              onKeyDown={e => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleAddNote();
              }}
            />
            <div className="flex items-center justify-between mt-2">
              <span className="text-xs text-gray-400">Ctrl+Enter to save</span>
              <button
                onClick={handleAddNote}
                disabled={!newNote.trim() || saving}
                className="px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {saving ? 'Saving...' : 'Add Note'}
              </button>
            </div>
          </div>

          {/* Notes list */}
          <div className="flex-1 overflow-y-auto">
            {notes.length === 0 ? (
              <div className="text-center py-8 text-gray-400 text-sm">No notes yet</div>
            ) : (
              <div className="divide-y divide-gray-50">
                {notes.map(note => (
                  <div key={note.id} className="px-4 py-3 hover:bg-gray-50 group">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-xs font-semibold">
                          {(note.first_name?.[0] || note.user_email?.[0] || '?').toUpperCase()}
                        </div>
                        <span className="text-sm font-medium text-gray-700">
                          {note.first_name ? `${note.first_name} ${note.last_name || ''}`.trim() : note.user_email}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-gray-400">{formatTime(note.created_at)}</span>
                        {note.created_by === user?.id && (
                          <div className="opacity-0 group-hover:opacity-100 flex items-center gap-0.5 ml-1">
                            <button
                              onClick={() => { setEditingId(note.id); setEditContent(note.content); }}
                              className="p-1 text-gray-400 hover:text-blue-600 rounded"
                              title="Edit"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleDeleteNote(note.id)}
                              className="p-1 text-gray-400 hover:text-red-600 rounded"
                              title="Delete"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    {editingId === note.id ? (
                      <div className="ml-8">
                        <textarea
                          value={editContent}
                          onChange={e => setEditContent(e.target.value)}
                          rows={2}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none resize-none"
                          autoFocus
                        />
                        <div className="flex gap-2 mt-1">
                          <button
                            onClick={() => handleUpdateNote(note.id)}
                            className="px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => { setEditingId(null); setEditContent(''); }}
                            className="px-2 py-1 text-gray-500 text-xs hover:text-gray-700"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-600 ml-8 whitespace-pre-line">{note.content}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : (
        /* Activity timeline */
        <div className="flex-1 overflow-y-auto">
          {activity.length === 0 ? (
            <div className="text-center py-8 text-gray-400 text-sm">No activity yet</div>
          ) : (
            <div className="px-4 py-3">
              <div className="relative">
                {/* Timeline line */}
                <div className="absolute left-3 top-2 bottom-2 w-px bg-gray-200" />

                <div className="space-y-4">
                  {activity.map(item => (
                    <div key={item.id} className="flex items-start gap-3 relative">
                      <div className="w-6 h-6 rounded-full bg-gray-100 border-2 border-white flex items-center justify-center flex-shrink-0 z-10">
                        <ActivityIcon action={item.action} />
                      </div>
                      <div className="flex-1 min-w-0 pt-0.5">
                        <p className="text-sm text-gray-700">
                          <span className="font-medium">
                            {item.first_name ? `${item.first_name} ${item.last_name || ''}`.trim() : 'System'}
                          </span>{' '}
                          <span className="text-gray-500">{getActivityLabel(item.action, item.details)}</span>
                        </p>
                        <p className="text-xs text-gray-400 mt-0.5">{formatTime(item.created_at)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};


const ActivityIcon: React.FC<{ action: string }> = ({ action }) => {
  if (action === 'note_added') {
    return (
      <svg className="w-3 h-3 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
      </svg>
    );
  }
  if (action.includes('bd_campaign')) {
    return (
      <svg className="w-3 h-3 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
      </svg>
    );
  }
  if (action === 'sent_to_sourcewhale') {
    return (
      <svg className="w-3 h-3 text-teal-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
      </svg>
    );
  }
  if (action.includes('pipeline') || action.includes('stage') || action.includes('shortlist')) {
    return (
      <svg className="w-3 h-3 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
      </svg>
    );
  }
  if (action === 'status_changed') {
    return (
      <svg className="w-3 h-3 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    );
  }
  return (
    <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
};


export default NotesActivity;
